import { useState } from 'react';

import type { TranslateFunction } from '@repo/phrase';
import type { AssetAttachment, DownloadAttachmentFunction, FormatDateTimeFunction } from '@repo/types/attachment';
import type { LogErrorFunction } from '@repo/utils';

import { NoBodyMessage } from '../message/message.component';
import { Paragraph } from '../typography/typography.component';
import { AttachmentEditSheet } from './attachment-edit-sheet.component';
import { AttachmentItem } from './attachment-item.component';
import { useAttachment } from './attachment.hooks';

import styles from './styles.module.scss';

interface Props {
    attachments: AssetAttachment[];
    translate: TranslateFunction;
    formatDateTime: FormatDateTimeFunction;
    downloadAttachment: DownloadAttachmentFunction;
    logError: LogErrorFunction;
    onDelete?: (attachment: AssetAttachment) => void;
    onComment?: (attachment: AssetAttachment) => void;
    isEditEnabled?: boolean;
}

interface AttachmentActionsProps {
    item: AssetAttachment;
    downloadAttachment: DownloadAttachmentFunction;
    logError: LogErrorFunction;
    onClose: () => void;
    onDelete?: ((attachment: AssetAttachment) => void) | undefined;
    onComment?: ((attachment: AssetAttachment) => void) | undefined;
    translate: TranslateFunction;
}

const AttachmentActions = ({
    item,
    downloadAttachment,
    logError,
    onClose,
    onDelete,
    onComment,
    translate,
}: AttachmentActionsProps) => {
    const { downloadFile, viewFile }: { downloadFile: () => Promise<void>; viewFile: () => Promise<void> } =
        useAttachment(item, downloadAttachment, logError);

    const handleOpenInNewTab = () => {
        onClose();
        void viewFile();
    };
    const handleComment = () => {
        onComment?.(item);
        onClose();
    };
    const handleDownload = () => {
        onClose();
        void downloadFile();
    };
    const handleDelete = () => {
        onDelete?.(item);
        onClose();
    };

    return (
        <AttachmentEditSheet
            visible
            onClose={onClose}
            onOpenInNewTab={handleOpenInNewTab}
            onDownload={handleDownload}
            translate={translate}
            {...(onComment ? { onComment: handleComment } : {})}
            {...(onDelete ? { onDelete: handleDelete } : {})}
        />
    );
};

export const AttachmentsList = ({
    attachments,
    translate,
    formatDateTime,
    downloadAttachment,
    logError,
    onDelete,
    onComment,
    isEditEnabled,
}: Props) => {
    const [selectedAttachment, setSelectedAttachment] = useState<AssetAttachment | null>(null);

    const createEditClickHandler = isEditEnabled
        ? (item: AssetAttachment) => () => {
              setSelectedAttachment(item);
          }
        : undefined;

    if (attachments.length === 0) {
        return (
            <NoBodyMessage
                title={translate('attachments_not_found_title')}
                body={translate('attachments_not_found_body')}
            />
        );
    }

    return (
        <div className={styles.attachment_table}>
            <div className={styles.attachment_table_header}>
                <div>
                    <Paragraph bodySize="s">{translate('attachments_file')}</Paragraph>
                </div>
                <div>
                    <Paragraph bodySize="s">{translate('attachments_upload')}</Paragraph>
                </div>
                <div>
                    <Paragraph bodySize="s" />
                </div>
                <div>
                    <Paragraph bodySize="s" />
                </div>
            </div>
            {attachments.map((item) => (
                <div key={JSON.stringify(item)} className={styles.attachment_table_row}>
                    <AttachmentItem
                        item={item}
                        translate={translate}
                        formatDateTime={formatDateTime}
                        downloadAttachment={downloadAttachment}
                        logError={logError}
                        downloadButtonLabel="asset_download_button"
                        onEditClick={createEditClickHandler?.(item)}
                    />
                </div>
            ))}
            {selectedAttachment && (
                <AttachmentActions
                    item={selectedAttachment}
                    downloadAttachment={downloadAttachment}
                    logError={logError}
                    onClose={() => {
                        setSelectedAttachment(null);
                    }}
                    onDelete={onDelete}
                    onComment={onComment}
                    translate={translate}
                />
            )}
        </div>
    );
};
